/**
 * EL ESTADO DEL FORMULARIO DE CONFIRMAR, compartido por las tres pantallas.
 *
 * Junta la propuesta de foco (`useFocusLinkProposal`) con lo que elige quien
 * confirma —gravedad, avance y foco— y devuelve dos cosas: lo que pinta
 * `ConfirmSamplingFields` y `buildInput()`, que es lo que se le pasa a
 * `useConfirmScoutingSession`. Las decisiones están en `confirmForm.ts` (sin
 * React, con sus pruebas); aquí solo se guarda el estado.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useFocusLinkProposal, type SamplingPlace } from "@/hooks/useFocusLinkProposal";
import type { ConfirmInput, ConfirmSeverity, ConfirmVerdict } from "@/hooks/useConfirmScoutingSession";
import { blockedBy, closedBeforeSampling, fieldStateOf, initialChoice, modeOf, optionsOf, verdictConflict, type ClosedFocusChoice, type FocusChoice } from "@/lib/pests/confirmForm";

const newRequestId = () =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : "xxxxxxxx-xxxx-4xxx-8xxx-xxxxxxxxxxxx".replace(/x/g, () => Math.floor(Math.random() * 16).toString(16));

export interface ConfirmFormArgs {
  /** Los focos están encendidos para esta empresa Y la pantalla está abierta. */
  enabled: boolean;
  place: SamplingPlace | null;
  pestId: string | null;
  pestName: string | null;
  /** La gravedad que el parte ya trae (la dijo quien muestreó). */
  initialSeverity?: ConfirmSeverity | null;
  /** La que sugiere la IA: llega elegida si el parte no trae otra, y se dice que es suya. */
  aiSeverity?: ConfirmSeverity | null;
  /** Lo que contestó quien estaba delante de la planta. */
  initialVerdict?: ConfirmVerdict | null;
  /**
   * La pantalla YA tiene su propio selector de gravedad (el asistente de captura):
   * manda el suyo, y aquí no se guarda ni se pinta otro. `undefined` = no lo tiene.
   */
  controlledSeverity?: ConfirmSeverity | null;
}

const SEVERITIES: readonly string[] = ["low", "medium", "high", "critical"];
const asSeverity = (s: string | null | undefined): ConfirmSeverity | null => (s && SEVERITIES.includes(s) ? (s as ConfirmSeverity) : null);

export function useConfirmSamplingForm({ enabled, place, pestId, pestName, initialSeverity, aiSeverity, initialVerdict, controlledSeverity }: ConfirmFormArgs) {
  const proposal = useFocusLinkProposal(place, pestId, enabled);
  const { result, loading, error, refetch, seenFocusIds, focusNumber } = proposal;

  const [ownSeverity, setSeverityState] = useState<ConfirmSeverity | null>(null);
  const [severityTouched, setSeverityTouched] = useState(false);
  const [verdict, setVerdict] = useState<ConfirmVerdict | null>(null);
  const [choice, setChoiceState] = useState<FocusChoice>({ kind: "unset" });
  const [touched, setTouched] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [closedChoice, setClosedChoice] = useState<ClosedFocusChoice | null>(null);
  const requestId = useRef(newRequestId());

  // Al abrir (o al cambiar de parte): formulario limpio y un id de petición nuevo.
  const formKey = `${enabled ? 1 : 0}|${place?.sessionId ?? ""}|${place?.blockId ?? ""}|${place?.rowId ?? ""}|${place?.lineNumber ?? ""}`;
  useEffect(() => {
    requestId.current = newRequestId();
    setSeverityTouched(false);
    setTouched(false);
    setExpanded(false);
    setVerdict(initialVerdict ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formKey]);

  // La gravedad llega elegida con lo que hay: la del parte, o la de la IA.
  const suggested = asSeverity(initialSeverity) ?? asSeverity(aiSeverity);
  useEffect(() => {
    if (!severityTouched) setSeverityState(suggested);
  }, [suggested, severityTouched, formKey]);

  // Otra plaga = otra propuesta: lo elegido a mano para la anterior ya no vale.
  useEffect(() => {
    setTouched(false);
    setExpanded(false);
  }, [pestId]);

  const proposalKey = result ? JSON.stringify(result.proposal) : "";
  useEffect(() => {
    if (!touched && result) setChoiceState(initialChoice(result.proposal));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposalKey, touched]);

  const setSeverity = useCallback((s: ConfirmSeverity) => {
    setSeverityTouched(true);
    setSeverityState(s);
  }, []);
  const setChoice = useCallback((c: FocusChoice) => {
    setTouched(true);
    setChoiceState(c);
  }, []);

  const severityControlled = controlledSeverity !== undefined;
  const severity = severityControlled ? asSeverity(controlledSeverity) : ownSeverity;
  const options = useMemo(() => (result ? optionsOf(result) : []), [result]);
  const chosen = choice.kind === "join" ? options.find((o) => o.candidate?.focus.id === choice.focusId)?.candidate ?? null : null;
  const hasBlock = !!place?.blockId;
  const proposalReady = !!result && !loading && !error;
  // D: el foco elegido se cerró DESPUÉS de este muestreo → hay que contestar si se reabre.
  const closedOn = hasBlock ? closedBeforeSampling(chosen, place?.date) : null;
  // Otra elección de foco = otra pregunta: lo contestado para el anterior no vale.
  const chosenId = chosen?.focus.id ?? null;
  useEffect(() => {
    setClosedChoice(null);
  }, [chosenId, formKey]);
  const blocked = blockedBy({ pestId, severity, choice, hasBlock, proposalReady, closedUnanswered: !!closedOn && !closedChoice });

  const buildInput = useCallback(
    (sessionId: string): ConfirmInput | null => {
      if (!severity) return null;
      if (hasBlock && (!result || choice.kind === "unset")) return null;
      const join = hasBlock && choice.kind === "join" ? choice : null;
      return {
        sessionId,
        pestId,
        pestName: join?.adopt ? null : pestName,
        severity,
        verdict: join ? verdict : null,
        focusAction: !hasBlock ? "none" : join ? "join" : "new",
        focusId: join?.focusId ?? null,
        adoptFocusPest: join?.adopt ?? false,
        seenFocusIds: seenFocusIds ?? [],
        clientRequestId: requestId.current,
        mode: result ? modeOf(result.proposal, choice) : "new",
        candidates: result?.candidates ?? [],
        proposalKind: result?.proposal.kind ?? "none",
        closedFocus: join && closedOn ? closedChoice : null,
      };
    },
    [severity, hasBlock, result, choice, pestId, pestName, verdict, seenFocusIds, closedOn, closedChoice],
  );

  return {
    enabled,
    hasBlock,
    loading: enabled && hasBlock && loading,
    loadError: enabled && hasBlock ? error : null,
    refetch,
    result: result ?? null,
    fieldState: result ? fieldStateOf(result.proposal) : null,
    options,
    choice,
    setChoice,
    chosen,
    expanded,
    setExpanded,
    severity,
    setSeverity,
    /** La gravedad que se ve es la que sugirió la IA y nadie la ha tocado. */
    severityIsAi: !severityControlled && !severityTouched && !asSeverity(initialSeverity) && !!asSeverity(aiSeverity) && severity === asSeverity(aiSeverity),
    verdict,
    setVerdict,
    /** Solo hay «respecto al anterior» si hay anterior: al unirse a un foco. */
    showVerdict: choice.kind === "join",
    conflict: verdictConflict(choice.kind === "join" ? verdict : null, severity, chosen?.focus.lastSeverity ?? null),
    focusNumber: focusNumber ?? new Map<string, number>(),
    hint: place?.hint ?? null,
    /** El día en que se cerró el foco elegido, si este muestreo es de antes (D). */
    closedOn,
    closedChoice,
    setClosedChoice,
    blocked,
    buildInput,
  };
}

export type ConfirmSamplingForm = ReturnType<typeof useConfirmSamplingForm>;
